import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import { statesAndDistricts } from '../../data/districts';
import './Agents.css';

const SuperAdminAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentShops, setAgentShops] = useState([]);
  const [showShopsModal, setShowShopsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [loadingShops, setLoadingShops] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    address: '',
    district: ''
  });

  useEffect(() => {
    fetchAgents();
  }, [pagination.page, search]);

  const fetchAgents = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      if (search) {
        params.append('search', search);
      }
      const response = await api.get(`/api/super/agents?${params}`);
      setAgents(response.data.data || []);
      setPagination({
        ...pagination,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      });
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewShops = async (agentId) => {
    setSelectedAgent(agents.find(a => a._id === agentId));
    setShowShopsModal(true);
    setLoadingShops(true);
    try {
      const response = await api.get(`/api/super/agents/${agentId}`);
      setAgentShops(response.data.data.shops || []);
    } catch (error) {
      console.error('Error fetching agent shops:', error);
    } finally {
      setLoadingShops(false);
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setEditForm({
      name: agent.name || '',
      phone: agent.phone || '',
      address: agent.address || '',
      district: agent.district || ''
    });
    setShowEditModal(true);
    setMessage({ type: '', text: '' });
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put(`/api/super/agents/${editingAgent._id}`, editForm);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Agent updated successfully!' });
        setShowEditModal(false);
        fetchAgents();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error updating agent'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (agent) => {
    setEditingAgent(agent);
    setShowDeleteConfirm(true);
    setMessage({ type: '', text: '' });
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.delete(`/api/super/agents/${editingAgent._id}`);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Agent deleted successfully!' });
        setShowDeleteConfirm(false);
        fetchAgents();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting agent'
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Agents Management</h1>
          <p className="page-subtitle">View and manage all agents and their registered shops</p>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
            {message.text}
          </div>
        )}

        <div className="filters-bar">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search agents by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            />
          </div>
        </div>

        <div className="stats-overview">
          <div className="stat-box">
            <div className="stat-label">Total Agents</div>
            <div className="stat-value">{pagination.total}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Total Shops by Agents</div>
            <div className="stat-value">
              {agents.reduce((sum, agent) => sum + (agent.shopCount || 0), 0)}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2>All Agents</h2>
          </div>
          <div className="table-container">
            {agents.length === 0 ? (
              <div className="empty-state">
                <p>No agents found.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>District</th>
                    <th>Total Shops</th>
                    <th>Active Shops</th>
                    <th>Registered Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent._id}>
                      <td>
                        <div className="agent-name">
                          <span className="agent-icon">👤</span>
                          {agent.name || 'N/A'}
                        </div>
                      </td>
                      <td>{agent.email}</td>
                      <td>{agent.phone || 'N/A'}</td>
                      <td>
                        {agent.district ? (
                          <span className="badge badge-secondary">{agent.district}</span>
                        ) : (
                          <span style={{ color: '#999' }}>Not assigned</span>
                        )}
                      </td>
                      <td>
                        <span className="badge badge-info">{agent.shopCount || 0}</span>
                      </td>
                      <td>
                        <span className="badge badge-success">{agent.activeShopCount || 0}</span>
                      </td>
                      <td>{new Date(agent.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleViewShops(agent._id)}
                          >
                            View Shops
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(agent)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(agent)}
                            disabled={agent.shopCount > 0}
                            title={agent.shopCount > 0 ? 'Cannot delete agent with registered shops' : 'Delete agent'}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="btn btn-sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {showShopsModal && (
        <div className="modal-overlay" onClick={() => setShowShopsModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Shops by {selectedAgent?.name || selectedAgent?.email}
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowShopsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingShops ? (
                <div className="loading"><div className="spinner"></div></div>
              ) : agentShops.length === 0 ? (
                <div className="empty-state">
                  <p>No shops registered by this agent yet.</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Shop Name</th>
                      <th>District</th>
                      <th>State</th>
                      <th>Admin Email</th>
                      <th>Status</th>
                      <th>Registered Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentShops.map((shop) => (
                      <tr key={shop._id}>
                        <td>
                          <div className="shop-info">
                            {shop.logoUrl && (
                              <img src={shop.logoUrl} alt={shop.name} className="shop-logo" />
                            )}
                            <span>{shop.name}</span>
                          </div>
                        </td>
                        <td>{shop.district}</td>
                        <td>{shop.state}</td>
                        <td>{shop.adminEmail || 'N/A'}</td>
                        <td>
                          <span className={`badge ${shop.isActive ? 'badge-success' : 'badge-danger'}`}>
                            {shop.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(shop.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && editingAgent && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Agent</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateAgent} className="modal-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingAgent.email}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>Email cannot be changed</small>
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>District *</label>
                <select
                  value={editForm.district}
                  onChange={(e) => setEditForm({ ...editForm, district: e.target.value })}
                  required
                >
                  <option value="">Select District</option>
                  {Object.entries(statesAndDistricts).map(([state, districts]) => (
                    districts.map(district => (
                      <option key={`${state}-${district}`} value={district}>{district}, {state}</option>
                    ))
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Update Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && editingAgent && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Agent</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              {message.text && (
                <div className={`alert alert-${message.type}`} style={{ marginBottom: '15px' }}>
                  {message.text}
                </div>
              )}
              {editingAgent.shopCount > 0 ? (
                <div className="alert alert-error">
                  <p><strong>Cannot Delete Agent</strong></p>
                  <p>This agent has {editingAgent.shopCount} registered shop(s). Please reassign or delete the shops first before deleting the agent.</p>
                </div>
              ) : (
                <>
                  <p>Are you sure you want to delete this agent?</p>
                  <div className="delete-info">
                    <p><strong>Name:</strong> {editingAgent.name || 'N/A'}</p>
                    <p><strong>Email:</strong> {editingAgent.email}</p>
                    {editingAgent.district && (
                      <p><strong>District:</strong> {editingAgent.district}</p>
                    )}
                  </div>
                  <p style={{ color: '#dc3545', fontWeight: '600', marginTop: '15px' }}>
                    This action cannot be undone.
                  </p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              {editingAgent.shopCount === 0 && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Agent'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAgents;

